import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { type ClosedPosition, type HistoryResponse, HistoryResponseSchema } from '../../api';
import { type ApiError, toApiError } from '../../lib/api/api-client';
import { useApiClient } from '../../lib/api/api-context';
import { useApiQuery } from '../../lib/api/use-api-query';
import { useAppVisible } from '../../lib/telegram/use-app-visible';

import { mergeHistory } from './history-model';

/** Rows asked per page (M6 R8; proposal, to be validated, Q58). C1 accepts 1 to 100. */
const HISTORY_PAGE_SIZE = 20;

/** What E9 renders. */
export interface HistoryQuery {
  /** Rows on screen, newest first, one per id; `undefined` until the first page arrives. */
  readonly positions: readonly ClosedPosition[] | undefined;
  /**
   * When the first page last arrived (device clock): "Today" and "Yesterday" are judged on it,
   * so the day groups follow each refresh without reading the clock while rendering.
   */
  readonly receivedAt: number | undefined;
  /** Cursor of the next page; `null` when there is none (the "Show more" button goes, AC12). */
  readonly nextCursor: string | null;
  /** Failure of the first page (no rows yet) or of its refresh (rows kept, spec §4 "Error"). */
  readonly error: ApiError | undefined;
  /** Retry of the first page. */
  readonly refetch: () => void;
  /** `true` while a load of the first page is in flight with an answer on screen: Retry is busy. */
  readonly retrying: boolean;
  /** Loads the next page and appends it ("Show more", R8). */
  readonly loadMore: () => void;
  /** `true` while a "Show more" page is in flight. */
  readonly loadingMore: boolean;
  /**
   * `true` from a refused cursor until the reloaded first page answers (M6 §8 "Rejected cursor"):
   * the cursor on screen is still the refused one, so "Show more" is busy and sends nothing.
   */
  readonly recovering: boolean;
  /** Failure of the last "Show more"; tapping the button again retries it. */
  readonly loadMoreError: ApiError | undefined;
}

/** Rows gathered by "Show more", with the cursor after them. */
interface Older {
  readonly positions: readonly ClosedPosition[];
  readonly nextCursor: string | null;
}

function historyPath(cursor?: string): string {
  const query = new URLSearchParams({ limit: String(HISTORY_PAGE_SIZE) });
  if (cursor !== undefined) query.set('cursor', cursor);
  return `/history?${query.toString()}`;
}

/**
 * Closed positions from `GET /history` (M6 §5): the first page when the tab opens and again when
 * the app becomes visible (R9, AC14), never on a timer; "Show more" appends the next page (R8,
 * AC12). A refresh of the first page keeps every row already on screen (`mergeHistory`), so rows a
 * new close pushed out of the first page are not lost, and the cursor stays the one of the last
 * page loaded.
 */
export function useHistory(): HistoryQuery {
  const api = useApiClient();
  const load = useCallback(
    (signal: AbortSignal) => api.get(historyPath(), HistoryResponseSchema, signal),
    [api],
  );
  const first = useApiQuery(load);
  const refetchFirst = first.refetch;
  useRefetchWhenVisibleAgain(refetchFirst);

  const [older, setOlder] = useState<Older | undefined>(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState<ApiError | undefined>(undefined);
  const [recovering, setRecovering] = useState(false);
  // The recovery ends when the reloaded first page answers, success or failure: set in the same
  // batch as refetchFirst, so `refreshing` is already true on the first render where it is set.
  // Adjusted during render (React "storing information from previous renders"), not in an effect,
  // so no render shows the button free with the refused cursor.
  if (recovering && !first.refreshing) setRecovering(false);
  const moreController = useRef<AbortController | undefined>(undefined);
  // Last cursor the API refused: a cursor is recovered from silently at most once (M6 §8).
  const refusedCursor = useRef<string | undefined>(undefined);
  useEffect(
    () => () => {
      moreController.current?.abort();
    },
    [],
  );

  // Memoised: the merge sorts every row, and HistoryTab groups them by day on each new array.
  const positions = useMemo(
    () =>
      first.data === undefined
        ? undefined
        : mergeHistory(first.data.positions, older?.positions ?? []),
    [first.data, older],
  );
  const nextCursor = older === undefined ? (first.data?.nextCursor ?? null) : older.nextCursor;

  const loadMore = useCallback(() => {
    if (
      recovering ||
      nextCursor === null ||
      positions === undefined ||
      moreController.current !== undefined
    ) {
      return;
    }
    const controller = new AbortController();
    moreController.current = controller;
    setLoadingMore(true);
    setLoadMoreError(undefined);
    const shown = positions;
    const cursor = nextCursor;
    api.get(historyPath(cursor), HistoryResponseSchema, controller.signal).then(
      (page: HistoryResponse) => {
        if (controller.signal.aborted) return;
        moreController.current = undefined;
        // Everything on screen now is "older" than the next refresh of the first page.
        setOlder({ positions: mergeHistory(shown, page.positions), nextCursor: page.nextCursor });
        setLoadingMore(false);
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        moreController.current = undefined;
        setLoadingMore(false);
        const apiError = toApiError(error);
        if (isRejectedCursor(apiError) && refusedCursor.current !== cursor) {
          // The API no longer accepts the cursor (C1 AC5: tampered or, after Q58, expired):
          // retrying it can only fail again, so start over from the first page, which brings a
          // fresh cursor. Done once per cursor: if the fresh first page hands the same cursor and
          // the API refuses it again, the reason is shown with the button below instead of
          // looping. Proposal, to be validated (M6 §4 and §8 "Rejected cursor", with Q58).
          refusedCursor.current = cursor;
          setOlder(undefined);
          setLoadMoreError(undefined);
          setRecovering(true);
          refetchFirst();
          return;
        }
        setLoadMoreError(apiError);
      },
    );
  }, [api, nextCursor, positions, recovering, refetchFirst]);

  return {
    positions,
    receivedAt: first.receivedAt,
    nextCursor,
    error: first.error ?? first.refreshError,
    refetch: first.refetch,
    retrying: first.refreshing,
    loadMore,
    loadingMore,
    loadMoreError,
    recovering,
  };
}

/**
 * Calls `refetch` once each time the app comes back from hidden to visible (M6 R9, AC14; M7
 * visibility hook). Nothing runs on a timer, and nothing runs on the first render: the tab
 * already loads when it opens.
 */
function useRefetchWhenVisibleAgain(refetch: () => void): void {
  const visible = useAppVisible();
  const wasVisible = useRef(visible);
  useEffect(() => {
    if (visible && !wasVisible.current) refetch();
    wasVisible.current = visible;
  }, [visible, refetch]);
}

/** A 400 `VALIDATION_FAILED` on a "Show more" request: the cursor itself was refused (C1 AC5). */
function isRejectedCursor(error: ApiError): boolean {
  return error.status === 400 && error.reasonCode === 'VALIDATION_FAILED';
}
